import React, { useState } from "react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { FlowPrimaryButton } from "@/components/consultation-flow/ui";
import { CheckCircle, ChevronDown, ChevronUp } from "lucide-react";
import type { GatewayProgressionNode as GatewayProgressionNodeType } from "@/lib/pharmacyFirst/types";
import type { IOTFCutOffTable } from "@/lib/pharmacyFirst/types/iotf.types";

interface GatewayProgressionNodeProps {
  node: GatewayProgressionNodeType;
  isDarkMode?: boolean;
  onContinue: () => void;
  // IOTF reference table — only passed for adolescent BMI instruction screen
  iotfTable?: IOTFCutOffTable;
  ageMonths?: number;
  biologicalSex?: string;
}

export function GatewayProgressionNode({
  node,
  isDarkMode = false,
  onContinue,
  iotfTable,
  ageMonths,
  biologicalSex,
}: GatewayProgressionNodeProps) {
  const [showIotfTable, setShowIotfTable] = useState(false);

  const showIotfReference = !!(iotfTable && ageMonths && biologicalSex && biologicalSex !== "other");

  const patientCutOff = showIotfReference
    ? [...(iotfTable!.cutOffs)]
        .filter((c) => c.sex === biologicalSex)
        .sort((a, b) => b.ageMonths - a.ageMonths)
        .find((c) => c.ageMonths <= ageMonths!)
    : null;

  const patientAgeYears = ageMonths ? (ageMonths / 12).toFixed(1) : null;

  return (
    <div className="space-y-4">
      <Alert className="bg-blue-50 border-blue-200">
        <CheckCircle className="h-5 w-5 text-blue-600" />
        <AlertTitle className="text-lg font-bold mt-2 text-blue-900">
          {node.heading}
        </AlertTitle>
        <AlertDescription className="mt-2 text-base text-blue-800 whitespace-pre-line">
          {node.body}
        </AlertDescription>
      </Alert>

      {showIotfReference && (
        <div className={`rounded-lg border ${
          isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-gray-50"
        }`}>
          <button
            type="button"
            onClick={() => setShowIotfTable((v) => !v)}
            className={`w-full flex items-center justify-between px-4 py-3 text-sm font-medium ${
              isDarkMode ? "text-gray-200" : "text-gray-700"
            }`}
          >
            <span>IOTF BMI cut-off reference table</span>
            {showIotfTable
              ? <ChevronUp className="h-4 w-4" />
              : <ChevronDown className="h-4 w-4" />
            }
          </button>

          {showIotfTable && (
            <div className="px-4 pb-4">
              <p className={`text-xs mb-3 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                Source: Cole TJ et al. BMJ 2000;320:1240–1243. Cut-offs represent BMI equivalent to 30 kg/m² at age 18.
                Patient must meet or exceed the cut-off AND weigh above 60 kg.
              </p>

              {patientCutOff && (
                <div className={`mb-3 px-3 py-2 rounded-lg text-sm font-medium ${
                  isDarkMode
                    ? "bg-blue-900/30 border border-blue-700 text-blue-200"
                    : "bg-blue-50 border border-blue-200 text-blue-800"
                }`}>
                  Patient age {patientAgeYears} yrs ({biologicalSex}) —
                  applicable cut-off: <strong>BMI ≥{patientCutOff.bmiCutOff.toFixed(2)} kg/m²</strong>
                </div>
              )}

              <div className="overflow-x-auto">
                <table className={`w-full text-xs border-collapse ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}>
                  <thead>
                    <tr className={isDarkMode ? "bg-gray-700" : "bg-gray-100"}>
                      <th className="px-2 py-1.5 text-left font-medium border border-gray-300">Age (yrs)</th>
                      <th className="px-2 py-1.5 text-right font-medium border border-gray-300">Male cut-off</th>
                      <th className="px-2 py-1.5 text-right font-medium border border-gray-300">Female cut-off</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from(
                      new Set(iotfTable!.cutOffs.map((c) => c.ageMonths))
                    )
                      .sort((a, b) => a - b)
                      .map((am) => {
                        const male = iotfTable!.cutOffs.find(
                          (c) => c.ageMonths === am && c.sex === "male"
                        );
                        const female = iotfTable!.cutOffs.find(
                          (c) => c.ageMonths === am && c.sex === "female"
                        );
                        const isPatientRow =
                          patientCutOff && am === patientCutOff.ageMonths;
                        return (
                          <tr
                            key={am}
                            className={
                              isPatientRow
                                ? isDarkMode
                                  ? "bg-blue-900/40 font-semibold"
                                  : "bg-blue-50 font-semibold"
                                : isDarkMode
                                  ? "even:bg-gray-750"
                                  : "even:bg-gray-50"
                            }
                          >
                            <td className="px-2 py-1 border border-gray-300">
                              {(am / 12).toFixed(1)}
                              {isPatientRow && (
                                <span className={`ml-1 text-xs ${
                                  isDarkMode ? "text-blue-400" : "text-blue-600"
                                }`}>← patient</span>
                              )}
                            </td>
                            <td className="px-2 py-1 text-right border border-gray-300">
                              {male?.bmiCutOff.toFixed(2) ?? "—"}
                            </td>
                            <td className="px-2 py-1 text-right border border-gray-300">
                              {female?.bmiCutOff.toFixed(2) ?? "—"}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      <FlowPrimaryButton
        label="Continue"
        wrapperClassName="flex gap-2"
        onClick={onContinue}
        size="lg"
        className="w-full"
      />
    </div>
  );
}
